import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const createBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/createBeneficiaryAccount",
  async (values, { rejectWithValue }) => {
    const {
      bank_account,
      virtual,
      contact_no,
      email_id,
      gstin,
      ifsc_code,
      panNumber,
      account_name,
      // categories,
      category_ids,
      labels,
      recipient_email,
      navigate,
    } = values;

    const requestBody = {
      beneficiary_accounts: {
        bank_account_no: bank_account,
        virtual,
        contact_no,
        email: email_id,
        gstin,
        ifsc: ifsc_code,
        pan: panNumber,
        name: account_name,
        // category: categories?.length > 0 ? categories : null,
        category_ids,
        labels,
        recipient_email,
        navigate,
      },
    };

    try {
      const { data } = await axiosInstance.post(
        `${baseUrl}/accounts/beneficiary`,
        requestBody,
        { headers: getHeaders() },
      );
      return data;
    } catch (error) {
      const responseData = error.response?.data;
      const errs = responseData?.errors;
      const firstError =
        Array.isArray(errs) && errs[0]
          ? typeof errs[0] === "string"
            ? errs[0]
            : Object.values(errs[0])[0]
          : errs;

      const errorMessage =
        (typeof firstError === "string" && firstError) ||
        firstError?.base?.[0] ||
        (Array.isArray(firstError) && firstError[0]) ||
        errs?.bank_account_number?.base?.[0] ||
        errs?.bank_account_number?.[0] ||
        errs?.permanent_account_number?.[0] ||
        errs?.category?.[0] ||
        errs?.gstin_detail?.base?.[0] ||
        errs?.base?.[0] ||
        responseData?.message ||
        error.message;

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        errors: errs || null,
      });
    }
  },
);

export const updateBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/updateBeneficiaryAccount",

  async (values, { rejectWithValue }) => {
    const {
      id,
      pan,
      contact_no,
      email,
      gstin,
      // category,
      category_ids,
      name,
      recipient_email,
      linked_user_id,
      type_of_account,
      navigate,
    } = values;

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/edit_other_details`,
        {
          beneficiary_accounts: {
            pan,
            email,
            contact_no,
            gstin,
            // category,
            category_ids,
            name,
            recipient_email,
            linked_user_id,
            type_of_account,
            navigate,
          },
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      let errorMessage;

      if (error.response) {
        if (error.response.data?.errors?.user_unauthorized) {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "BUAccount" },
          });
        }
        errorMessage =
          error.response.data?.errors?.bank_account_number?.[0] ||
          error.response.data?.errors?.beneficiary_account?.[0] ||
          error.response.data?.errors?.permanent_account_number?.[0] ||
          error.response.data?.errors?.[0]?.gstin_detail?.base ||
          error.response.data?.errors?.base?.[0] ||
          error.response.data?.errors?.[0]?.gstin_detail?.base?.[0] ||
          error.response.data.error ||
          (Array.isArray(error.response.data?.errors?.category)
            ? error.response.data?.errors?.category[0]
            : undefined);
      } else {
        errorMessage = error.message;
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateBeneficiaryBankDetails = createAsyncThunk(
  "beneficiaryAccount/updateBeneficiaryBankDetails",
  async (values, { rejectWithValue }) => {
    const { id, bank_account, ifsc_code, name, navigate, virtual } = values;

    const requestBody = {
      beneficiary_accounts: {
        name,
        bank_account_no: bank_account,
        ifsc: ifsc_code,
        navigate,
        virtual,
      },
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}`,
        requestBody,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;

      if (responseData?.errors?.user_unauthorized) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "BUAccount" },
        });

        return rejectWithValue("User unauthorized");
      }

      const errorMessage =
        responseData?.errors?.category?.[0] ||
        responseData?.errors?.bank_account_number?.[0] ||
        responseData?.errors?.beneficiary_account?.[0] ||
        responseData?.errors?.[0]?.bank_account_number?.base?.[0] ||
        responseData?.errors?.permanent_account_number?.[0] ||
        responseData?.errors?.gstin_detail?.base ||
        responseData?.errors?.base?.[0] ||
        responseData?.message ||
        error.message;

      return rejectWithValue(errorMessage);
    }
  },
);

export const getAllBeneficiaryAccounts = createAsyncThunk(
  "beneficiaryAccount/getAllBeneficiaryAccounts",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_size,
      approval_status,
      category_ids,
      search_string,
      bank_account_no,
      ifsc,
      start_date,
      end_date,
      pan,
      type,
      labels,
      associate,
      type_of_account,
    } = values;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary`,
        {
          headers: getHeaders(),
          params: {
            page_no,
            page_size,
            approval_status: associate
              ? associate
              : Array.isArray(approval_status)
                ? approval_status
                : approval_status
                  ? [approval_status]
                  : type === "homeDashboard"
                    ? []
                    : "",
            category_ids: category_ids,
            search_string,
            start_date,
            end_date,
            bank_account_no,
            ifsc,
            pan,
            labels,
            type_of_account,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addBeneficiaryAccountLabel = createAsyncThunk(
  "beneficiaryAccount/addBeneficiaryAccountLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/add_label`,
        data,
        {
          headers: getHeaders(),
        },
      );
      toast.dismiss();

      const existingLabels = JSON?.parse(
        sessionStorage?.getItem("beneficiaryAccountLabels") || [],
      );

      const updatedLabels = Array?.from(
        new Set([...existingLabels, ...labels]),
      );

      const payload = JSON?.stringify(updatedLabels);
      sessionStorage?.setItem("beneficiaryAccountLabels", payload);

      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base?.[0]) {
        CommonToast({
          text: error?.response?.data?.errors?.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      if (error?.response?.data?.errors?.user_unauthorized?.[0]) {
        CommonToast({
          text: error.response.data.errors.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const removeBeneficiaryAccountLabel = createAsyncThunk(
  "beneficiaryAccount/removeBeneficiaryAccountLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/remove_label`,
        data,
        {
          headers: getHeaders(),
        },
      );

      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base) {
        CommonToast({
          text: error.response.data.errors.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      } else {
        CommonToast({
          text: error?.response?.data?.errors?.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const beneficiaryAccountDetailsById = createAsyncThunk(
  "beneficiaryAccount/beneficiaryAccountDetailsById",

  async (values) => {
    const { id } = values ? values : { id: null };
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary/${id}`,
        {
          headers: getHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const approveBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/approveBeneficiaryAccount",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/approve`,
        {},
        {
          headers: getHeaders(),
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const rejectBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/rejectBeneficiaryAccount",
  async ({ id, rejection_reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/reject`,
        {
          rejection_reason,
        },
        {
          headers: getHeaders(),
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const archiveBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/archiveBeneficiaryAccount",
  async ({ id, Navigate }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/archive`,
        {},
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response?.data?.errors?.user_unauthorized) {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "BAAccountArchive",
          },
        });
        return;
      }

      if (
        error?.response?.data?.errors?.reimbursement_account ||
        error?.response?.data?.errors?.beneficiary_account
      ) {
        let errorMessage =
          error?.response?.data?.errors?.reimbursement_account?.[0] ||
          error?.response?.data?.errors?.beneficiary_account?.[0];
        CommonToast({
          text: errorMessage,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else if (
        error?.response?.data?.errors?.bank_account_number ||
        error?.response?.data?.errors?.permanent_account_number
      ) {
        let errorMessage =
          error?.response?.data?.errors?.bank_account_number[0] ||
          error?.response?.data?.errors?.permanent_account_number[0];
        CommonToast({
          text: errorMessage,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      let errorMessage =
        error?.response?.data?.errors?.bank_account_number[0] ||
        error?.response?.data?.errors?.beneficiary_account?.[0] ||
        error?.response?.data?.errors?.permanent_account_number[0] ||
        error?.response?.data?.errors[0]?.gstin_detail?.base ||
        error?.response?.data?.errors?.base[0];
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

export const unarchiveBeneficiaryAccount = createAsyncThunk(
  "beneficiaryAccount/unarchiveBeneficiaryAccount",
  async ({ id, Navigate }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/unarchive`,
        {
          headers: getHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      if (error?.response?.data?.errors?.user_unauthorized) {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "BAAccountArchive",
          },
        });
        return;
      }
      const errData = error?.response?.data?.errors;

      if (!errData.base) {
        errData.beneficiary_account.forEach((error3) => {
          if (error?.response?.data.status === false) {
            setTimeout(() => {
              CommonToast({
                text: error3,
                icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
                variant: "var(--error)",
                buttonNeeded: false,
              });
            }, 1000);
          }
        });
      } else {
        CommonToast({
          text: errData.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      return rejectWithValue(errData);
    }
  },
);

export const unAssignCategories = createAsyncThunk(
  "unAssignCategories",
  async (values) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          params: {
            page_no: values.page_no,
            approval_status: ["pending", "approved", "rejected"],
            category_ids: values.category_ids,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

const beneficiaryAccountSlice = createSlice({
  name: "beneficiaryAccount",

  initialState: {
    toggle: false,

    createBeneficiaryAccountData: null,
    createBeneficiaryAccountLoading: false,
    createBeneficiaryAccountError: false,

    updateBeneficiaryAccountData: null,
    updateBeneficiaryAccountLoading: false,
    updateBeneficiaryAccountError: false,

    updateBeneficiaryBankData: null,
    updateBeneficiaryBankLoading: false,
    updateBeneficiaryBankError: false,

    beneficiaryAccountDetailsData: null,
    beneficiaryAccountDetailsLoading: false,
    beneficiaryAccountDetailsError: false,

    getAllBeneficiaryAccountsData: null,
    getAllBeneficiaryAccountsLoading: false,
    getAllBeneficiaryAccountsError: false,

    beneficiaryAccountAvailableLabels: [],

    addLabelsBeneAccountData: null,
    addLabelsBeneAccountLoading: false,
    addLabelsBeneAccountError: false,

    removeLabelsBeneAccountData: null,
    removeLabelsBeneAccountLoading: false,
    removeLabelsBeneAccountError: false,

    approveBeneficiaryAccountData: null,
    approveBeneficiaryAccountLoading: false,
    approveBeneficiaryAccountError: false,

    rejectBeneficiaryAccountData: null,
    rejectBeneficiaryAccountLoading: false,
    rejectBeneficiaryAccountError: false,

    archiveBeneficiaryAccountData: null,
    archiveBeneficiaryAccountLoading: false,
    archiveBeneficiaryAccountError: false,

    unarchiveBeneficiaryAccountData: null,
    unarchiveBeneficiaryAccountLoading: false,
    unarchiveBeneficiaryAccountError: false,

    unAssignCategoryData: null,
    unAssignCategoryLoading: false,
  },

  reducers: {
    setBeneficiaryToggle: (state, action) => {
      state.toggle = action.payload;
    },
    resetBeneficiaryToggle: (state) => {
      state.toggle = false;
    },
    resetCreateBeneficiaryAccount(state) {
      state.createBeneficiaryAccountData = null;
      state.createBeneficiaryAccountError = false;
    },

    resetUpdateBeneficiaryAccount(state) {
      state.updateBeneficiaryAccountData = null;
      state.updateBeneficiaryAccountError = false;
    },

    resetBeneficiaryBankUpdate(state) {
      state.updateBeneficiaryBankData = null;
      state.updateBeneficiaryBankError = false;
    },

    resetBeneficiaryAccountDetailsById(state) {
      state.beneficiaryAccountDetailsData = null;
      state.beneficiaryAccountDetailsLoading = false;
      state.beneficiaryAccountDetailsError = false;
    },

    resetBeneficiaryAccountViewAll(state) {
      state.getAllBeneficiaryAccountsData = null;
      state.getAllBeneficiaryAccountsLoading = false;
      state.getAllBeneficiaryAccountsError = false;
    },

    resetBeneficiaryLabelLoading: (state) => {
      state.addBeneficiaryLabelLoading = false;
      state.removeBeneficiaryLabelLoading = false;
    },

    setBeneficiaryAvailabelLabels: (state, action) => {
      state.beneficiaryAccountAvailableLabels = action.payload;
    },
    resetBeneficiaryAccountLabel(state) {
      state.addLabelsBeneAccountLoading = null;
      state.removeLabelsBeneAccountLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createBeneficiaryAccount.pending, (state) => {
        state.createBeneficiaryAccountLoading = true;
      })
      .addCase(createBeneficiaryAccount.fulfilled, (state, action) => {
        state.createBeneficiaryAccountLoading = false;
        state.createBeneficiaryAccountData = action.payload;
      })
      .addCase(createBeneficiaryAccount.rejected, (state) => {
        state.createBeneficiaryAccountLoading = false;
        state.createBeneficiaryAccountError = true;
      })
      .addCase(updateBeneficiaryAccount.pending, (state) => {
        state.updateBeneficiaryAccountLoading = true;
      })
      .addCase(updateBeneficiaryAccount.fulfilled, (state, action) => {
        state.updateBeneficiaryAccountLoading = false;
        state.updateBeneficiaryAccountData = action.payload;
      })
      .addCase(updateBeneficiaryAccount.rejected, (state) => {
        state.updateBeneficiaryAccountLoading = false;
        state.updateBeneficiaryAccountError = true;
      })
      .addCase(updateBeneficiaryBankDetails.pending, (state) => {
        state.updateBeneficiaryBankLoading = true;
      })
      .addCase(updateBeneficiaryBankDetails.fulfilled, (state, action) => {
        state.updateBeneficiaryBankLoading = false;
        state.updateBeneficiaryBankData = action.payload;
      })
      .addCase(updateBeneficiaryBankDetails.rejected, (state, action) => {
        state.updateBeneficiaryBankLoading = false;
        state.updateBeneficiaryBankError = true;
        state.updateBeneficiaryBankData = {
          status: false,
          errors: { base: [action.payload] },
        };
      })
      .addCase(beneficiaryAccountDetailsById.pending, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.beneficiaryAccountAvailableLabels = [];
        }
        state.beneficiaryAccountDetailsLoading = true;
      })
      .addCase(beneficiaryAccountDetailsById.fulfilled, (state, action) => {
        state.beneficiaryAccountDetailsLoading = false;
        state.beneficiaryAccountDetailsData = action.payload;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "beneficiaryAccountLabels",
            JSON.stringify(action?.payload?.summary?.available_labels || []),
          );
        }
        state.beneficiaryAccountDetailsError = false;
      })
      .addCase(beneficiaryAccountDetailsById.rejected, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.beneficiaryAccountAvailableLabels = [];
        }
        state.beneficiaryAccountDetailsLoading = false;
        state.beneficiaryAccountDetailsError = action.payload;
      })
      .addCase(getAllBeneficiaryAccounts.pending, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.beneficiaryAccountAvailableLabels = [];
        }
        state.getAllBeneficiaryAccountsLoading = true;
      })
      .addCase(getAllBeneficiaryAccounts.fulfilled, (state, action) => {
        state.getAllBeneficiaryAccountsLoading = false;
        state.getAllBeneficiaryAccountsData = action.payload;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "beneficiaryAccountLabels",
            JSON.stringify(action?.payload?.summary?.available_labels || []),
          );
        }
        state.getAllBeneficiaryAccountsError = false;
      })
      .addCase(getAllBeneficiaryAccounts.rejected, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.beneficiaryAccountAvailableLabels = [];
        }
        state.getAllBeneficiaryAccountsLoading = false;
        state.getAllBeneficiaryAccountsError = true;
      })
      .addCase(addBeneficiaryAccountLabel.pending, (state) => {
        state.addLabelsBeneAccountLoading = true;
      })
      .addCase(addBeneficiaryAccountLabel.fulfilled, (state, action) => {
        state.addLabelsBeneAccountLoading = false;
        state.addLabelsBeneAccountData = action.payload;
        state.addLabelsBeneAccountError = false;
      })
      .addCase(addBeneficiaryAccountLabel.rejected, (state) => {
        state.addLabelsBeneAccountLoading = false;
        state.addLabelsBeneAccountError = true;
      })
      .addCase(removeBeneficiaryAccountLabel.pending, (state) => {
        state.removeLabelsBeneAccountLoading = true;
      })
      .addCase(removeBeneficiaryAccountLabel.fulfilled, (state, action) => {
        state.removeLabelsBeneAccountLoading = false;
        state.removeLabelsBeneAccountData = action.payload;
        state.removeLabelsBeneAccountError = false;
      })
      .addCase(removeBeneficiaryAccountLabel.rejected, (state) => {
        state.removeLabelsBeneAccountLoading = false;
        state.removeLabelsBeneAccountError = true;
      })
      .addCase(approveBeneficiaryAccount.pending, (state) => {
        state.approveBeneficiaryAccountLoading = true;
      })
      .addCase(approveBeneficiaryAccount.fulfilled, (state, action) => {
        state.approveBeneficiaryAccountLoading = false;
        state.approveBeneficiaryAccountData = action.payload;
        state.approveBeneficiaryAccountError = false;
      })
      .addCase(approveBeneficiaryAccount.rejected, (state) => {
        state.approveBeneficiaryAccountLoading = false;
        state.approveBeneficiaryAccountError = true;
      })
      .addCase(rejectBeneficiaryAccount.pending, (state) => {
        state.rejectBeneficiaryAccountLoading = true;
      })
      .addCase(rejectBeneficiaryAccount.fulfilled, (state, action) => {
        state.rejectBeneficiaryAccountLoading = false;
        state.rejectBeneficiaryAccountData = action.payload;
        state.rejectBeneficiaryAccountError = false;
      })
      .addCase(rejectBeneficiaryAccount.rejected, (state) => {
        state.rejectBeneficiaryAccountLoading = false;
        state.rejectBeneficiaryAccountError = true;
      })
      .addCase(archiveBeneficiaryAccount.pending, (state) => {
        state.archiveBeneficiaryAccountLoading = true;
      })
      .addCase(archiveBeneficiaryAccount.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          CommonToast({
            text: "Account archived successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
          });
        }
        state.archiveBeneficiaryAccountLoading = false;
        state.archiveBeneficiaryAccountData = action.payload;
        state.archiveBeneficiaryAccountError = false;
      })
      .addCase(archiveBeneficiaryAccount.rejected, (state) => {
        state.archiveBeneficiaryAccountLoading = false;
        state.archiveBeneficiaryAccountError = true;
      })
      .addCase(unarchiveBeneficiaryAccount.pending, (state) => {
        state.unarchiveBeneficiaryAccountLoading = true;
      })
      .addCase(unarchiveBeneficiaryAccount.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          CommonToast({
            text: "Account unarchived successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
          });
        }
        state.unarchiveBeneficiaryAccountLoading = false;
        state.unarchiveBeneficiaryAccountData = action.payload;
        state.unarchiveBeneficiaryAccountError = false;
      })
      .addCase(unarchiveBeneficiaryAccount.rejected, (state) => {
        state.unarchiveBeneficiaryAccountLoading = false;
        state.unarchiveBeneficiaryAccountError = true;
      })
      .addCase(unAssignCategories.pending, (state) => {
        state.unAssignCategoryLoading = true;
      })
      .addCase(unAssignCategories.fulfilled, (state, action) => {
        state.unAssignCategoryLoading = false;
        state.unAssignCategoryData = action.payload;
      })
      .addCase(unAssignCategories.rejected, (state) => {
        state.unAssignCategoryLoading = false;
      });
  },
});

export const {
  resetCreateBeneficiaryAccount,
  resetUpdateBeneficiaryAccount,
  resetBeneficiaryBankUpdate,
  resetBeneficiaryAccountViewAll,
  resetBeneficiaryLabelLoading,
  setBeneficiaryAvailabelLabels,
  resetBeneficiaryAccountDetailsById,
  setBeneficiaryToggle,
  resetBeneficiaryToggle,
  resetBeneficiaryAccountLabel,
} = beneficiaryAccountSlice.actions;

export default beneficiaryAccountSlice.reducer;
